import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingContactWidget from "@/components/FloatingContactWidget";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Calculator, Shield, TrendingUp, Download, ArrowRight, Check } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const calculatorSchema = z.object({
  name: z.string().trim().min(2, "Name erforderlich").max(100),
  email: z.string().trim().email("Ungültige E-Mail").max(255),
  phone: z.string().trim().min(6, "Telefonnummer erforderlich").max(20),
  practiceType: z.string().min(1, "Praxistyp erforderlich"),
  employees: z.number().min(1).max(500),
  revenue: z.number().min(50000).max(50000000),
  itBudget: z.number().min(0).max(1000000),
  hasBackup: z.boolean(),
  has2FA: z.boolean(),
  hasFirewall: z.boolean(),
  acceptsPrivacy: z.boolean().refine(val => val === true, "Datenschutz muss akzeptiert werden")
});

type CalculatorForm = z.infer<typeof calculatorSchema>;

const VersicherungsRechner = () => {
  const [step, setStep] = useState(1);
  const [employees, setEmployees] = useState([5]);
  const [revenue, setRevenue] = useState([250000]);
  const [itBudget, setItBudget] = useState([5000]);
  const [result, setResult] = useState<any>(null);
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CalculatorForm>({
    resolver: zodResolver(calculatorSchema),
    defaultValues: {
      employees: 5,
      revenue: 250000,
      itBudget: 5000,
      hasBackup: false,
      has2FA: false,
      hasFirewall: false,
      acceptsPrivacy: false
    }
  });

  const practiceType = watch("practiceType");
  const hasBackup = watch("hasBackup");
  const has2FA = watch("has2FA");
  const hasFirewall = watch("hasFirewall");

  const calculateRecommendation = (data: CalculatorForm) => {
    // Berechnung basierend auf Praxisgröße, Umsatz und Sicherheitsmaßnahmen
    const baseAmount = data.revenue * 0.02; // 2% vom Jahresumsatz als Basis
    const employeeFactor = 1 + (data.employees / 50); // Mehr Mitarbeiter = höheres Risiko
    
    let securityDiscount = 1.0;
    if (data.hasBackup) securityDiscount -= 0.15;
    if (data.has2FA) securityDiscount -= 0.10;
    if (data.hasFirewall) securityDiscount -= 0.10;
    
    const recommendedSum = Math.round(baseAmount * employeeFactor * securityDiscount);
    const cyberDamageSum = Math.round(recommendedSum * 0.3);
    const businessInterruptionSum = Math.round(recommendedSum * 0.35);
    const liabilitySum = Math.round(recommendedSum * 0.35);
    
    const monthlyPremium = Math.round((recommendedSum / 12000) * 100) / 100;
    
    // Tarifempfehlungen basierend auf Deckungssumme
    const tariffs = [];
    if (recommendedSum < 200000) {
      tariffs.push(
        { provider: "VHV Cyber-Protection", slug: "vhv", price: Math.round(monthlyPremium * 0.85), rating: "Sehr gut" },
        { provider: "Württembergische Cyber", slug: "wuerttembergische", price: Math.round(monthlyPremium * 0.90), rating: "Gut" },
        { provider: "Nürnberger Cyber-Schutz", slug: "nuernberger", price: Math.round(monthlyPremium * 0.95), rating: "Gut" }
      );
    } else if (recommendedSum < 500000) {
      tariffs.push(
        { provider: "Allianz Cyber-Schutz", slug: "allianz", price: Math.round(monthlyPremium * 0.95), rating: "Ausgezeichnet" },
        { provider: "ERGO Cyber Plus", slug: "ergo", price: Math.round(monthlyPremium * 1.0), rating: "Sehr gut" },
        { provider: "Gothaer Gewerbe-Protect", slug: "gothaer", price: Math.round(monthlyPremium * 1.05), rating: "Sehr gut" }
      );
    } else {
      tariffs.push(
        { provider: "Hiscox CyberClear", slug: "hiscox", price: Math.round(monthlyPremium * 1.0), rating: "Testsieger" },
        { provider: "Zurich Cyber Professional", slug: "zurich", price: Math.round(monthlyPremium * 1.05), rating: "Ausgezeichnet" },
        { provider: "AXA Cyber-Protection Premium", slug: "axa", price: Math.round(monthlyPremium * 1.10), rating: "Sehr gut" }
      );
    }
    
    return {
      recommendedSum,
      cyberDamageSum,
      businessInterruptionSum,
      liabilitySum,
      monthlyPremium,
      tariffs,
      securityScore: Math.round((1 - securityDiscount) * 100)
    };
  };

  const onSubmit = async (data: CalculatorForm) => {
    const recommendation = calculateRecommendation(data);
    setResult(recommendation);
    
    // Lead in Datenbank speichern
    try {
      const { error } = await supabase.from('consultation_requests').insert({
        email: data.email,
        phone: data.phone,
        practice_type: data.practiceType,
        employees: data.employees,
        postal_code: "00000", // Optional
        notes: `Versicherungsrechner: ${recommendation.recommendedSum}€ empfohlen`,
        source: "versicherungsrechner"
      });
      
      if (error) throw error;
      
      toast.success("Berechnung erfolgreich! Ihre Empfehlung wurde erstellt.");
      setStep(3);
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error("Fehler beim Speichern. Bitte versuchen Sie es erneut.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Versicherungssummen-Rechner | Optimale Cyberversicherung berechnen</title>
        <meta name="description" content="Berechnen Sie kostenlos die optimale Deckungssumme für Ihre Cyberversicherung. Individuelle Empfehlung basierend auf Praxisgröße, Umsatz und IT-Sicherheit." />
        <link rel="canonical" href="https://praxis-versichert.de/versicherungsrechner" />
      </Helmet>

      <Navigation />
      
      <main className="flex-grow py-12 bg-gradient-to-br from-background via-accent/5 to-background">
        <div className="container">
          {/* Hero */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Calculator className="h-5 w-5" />
              <span className="font-semibold">Kostenloser Versicherungsrechner</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Finden Sie Ihre optimale Cyberversicherung
            </h1>
            <p className="text-xl text-muted-foreground">
              Berechnen Sie in 3 Minuten die passende Deckungssumme und erhalten Sie personalisierte Tarifempfehlungen
            </p>
          </div>

          {/* Progress */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className={`flex items-center ${s < 3 ? 'flex-1' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {step > s ? <Check className="h-5 w-5" /> : s}
                  </div>
                  {s < 3 && <div className={`h-1 flex-1 mx-2 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Praxisdaten</span>
              <span>IT-Sicherheit</span>
              <span>Ergebnis</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Step 1: Praxisdaten */}
            {step === 1 && (
              <Card className="max-w-2xl mx-auto">
                <CardHeader>
                  <CardTitle>Schritt 1: Ihre Praxisdaten</CardTitle>
                  <CardDescription>Geben Sie grundlegende Informationen zu Ihrer Praxis an</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" {...register("name")} placeholder="Dr. Max Mustermann" />
                      {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input id="email" type="email" {...register("email")} placeholder="praxis@beispiel.de" />
                      {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input id="phone" {...register("phone")} placeholder="0123 456789" />
                      {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="practiceType">Praxistyp *</Label>
                      <Select onValueChange={(v) => setValue("practiceType", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Wählen Sie..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hausarzt">Hausarztpraxis</SelectItem>
                          <SelectItem value="zahnarzt">Zahnarztpraxis</SelectItem>
                          <SelectItem value="facharzt">Facharztpraxis</SelectItem>
                          <SelectItem value="mvz">MVZ</SelectItem>
                          <SelectItem value="klinik">Klinik</SelectItem>
                          <SelectItem value="physiotherapie">Physiotherapie</SelectItem>
                          <SelectItem value="apotheke">Apotheke</SelectItem>
                          <SelectItem value="psychotherapie">Psychotherapie</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.practiceType && <p className="text-sm text-destructive">{errors.practiceType.message}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Anzahl Mitarbeiter: {employees[0]}</Label>
                    <Slider 
                      value={employees} 
                      onValueChange={(v) => { setEmployees(v); setValue("employees", v[0]); }}
                      min={1} 
                      max={100} 
                      step={1} 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Jahresumsatz: {(revenue[0] / 1000).toFixed(0)}k €</Label>
                    <Slider 
                      value={revenue} 
                      onValueChange={(v) => { setRevenue(v); setValue("revenue", v[0]); }}
                      min={50000} 
                      max={5000000} 
                      step={10000} 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Jährliches IT-Budget: {(itBudget[0] / 1000).toFixed(1)}k €</Label>
                    <Slider 
                      value={itBudget} 
                      onValueChange={(v) => { setItBudget(v); setValue("itBudget", v[0]); }}
                      min={0} 
                      max={100000} 
                      step={1000} 
                    />
                  </div>

                  <Button type="button" className="w-full" onClick={() => setStep(2)}>
                    Weiter <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: IT-Sicherheit */}
            {step === 2 && (
              <Card className="max-w-2xl mx-auto">
                <CardHeader>
                  <CardTitle>Schritt 2: IT-Sicherheitsmaßnahmen</CardTitle>
                  <CardDescription>Welche Sicherheitsmaßnahmen sind bereits implementiert?</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3 p-4 rounded-lg border hover:bg-accent/50 transition-colors">
                      <Checkbox 
                        id="backup" 
                        checked={hasBackup}
                        onCheckedChange={(checked) => setValue("hasBackup", checked as boolean)}
                      />
                      <div className="flex-1">
                        <Label htmlFor="backup" className="font-semibold cursor-pointer">Regelmäßige Backups (3-2-1-Regel)</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Tägliche Backups auf 3 Medien, davon 1 offline/extern
                        </p>
                      </div>
                      {hasBackup && <span className="text-sm text-green-600 font-medium">-15% Prämie</span>}
                    </div>

                    <div className="flex items-start space-x-3 p-4 rounded-lg border hover:bg-accent/50 transition-colors">
                      <Checkbox 
                        id="2fa" 
                        checked={has2FA}
                        onCheckedChange={(checked) => setValue("has2FA", checked as boolean)}
                      />
                      <div className="flex-1">
                        <Label htmlFor="2fa" className="font-semibold cursor-pointer">Zwei-Faktor-Authentifizierung</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          2FA für alle kritischen Systeme aktiviert
                        </p>
                      </div>
                      {has2FA && <span className="text-sm text-green-600 font-medium">-10% Prämie</span>}
                    </div>

                    <div className="flex items-start space-x-3 p-4 rounded-lg border hover:bg-accent/50 transition-colors">
                      <Checkbox 
                        id="firewall" 
                        checked={hasFirewall}
                        onCheckedChange={(checked) => setValue("hasFirewall", checked as boolean)}
                      />
                      <div className="flex-1">
                        <Label htmlFor="firewall" className="font-semibold cursor-pointer">Professionelle Firewall</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Firewall mit Intrusion Prevention System
                        </p>
                      </div>
                      {hasFirewall && <span className="text-sm text-green-600 font-medium">-10% Prämie</span>}
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 rounded-lg border bg-accent/20">
                    <Checkbox 
                      id="privacy" 
                      onCheckedChange={(checked) => setValue("acceptsPrivacy", checked as boolean)}
                    />
                    <Label htmlFor="privacy" className="text-sm cursor-pointer">
                      Ich akzeptiere die Datenschutzerklärung und stimme der Kontaktaufnahme zu. *
                    </Label>
                  </div>
                  {errors.acceptsPrivacy && <p className="text-sm text-destructive">{errors.acceptsPrivacy.message}</p>}

                  <div className="flex gap-4">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>
                      Zurück
                    </Button>
                    <Button type="submit" className="flex-1">
                      Berechnen <TrendingUp className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Ergebnis */}
            {step === 3 && result && (
              <div className="max-w-4xl mx-auto space-y-6">
                <Card className="border-primary bg-gradient-to-br from-primary/5 to-transparent">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-6 w-6 text-primary" />
                      Ihre persönliche Empfehlung
                    </CardTitle>
                    <CardDescription>Optimale Deckungssummen basierend auf Ihrer Praxisgröße und IT-Sicherheit</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <div className="text-sm text-muted-foreground mb-2">Empfohlene Gesamtdeckung</div>
                        <div className="text-4xl font-bold text-primary">{result.recommendedSum.toLocaleString()} €</div>
                        <div className="text-sm text-muted-foreground mt-2">
                          Sicherheits-Score: <span className="font-semibold text-green-600">{result.securityScore}%</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-2">Geschätzte monatliche Prämie</div>
                        <div className="text-4xl font-bold">ab {result.monthlyPremium} €</div>
                        <div className="text-sm text-muted-foreground mt-2">
                          Abhängig vom gewählten Tarif
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mt-8">
                      <div className="p-4 rounded-lg bg-background border">
                        <div className="text-sm text-muted-foreground mb-1">Cyber-Eigenschaden</div>
                        <div className="text-2xl font-bold">{result.cyberDamageSum.toLocaleString()} €</div>
                      </div>
                      <div className="p-4 rounded-lg bg-background border">
                        <div className="text-sm text-muted-foreground mb-1">Betriebsunterbrechung</div>
                        <div className="text-2xl font-bold">{result.businessInterruptionSum.toLocaleString()} €</div>
                      </div>
                      <div className="p-4 rounded-lg bg-background border">
                        <div className="text-sm text-muted-foreground mb-1">Cyber-Haftpflicht</div>
                        <div className="text-2xl font-bold">{result.liabilitySum.toLocaleString()} €</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid md:grid-cols-3 gap-6">
                  {result.tariffs.map((tariff: any, idx: number) => (
                    <Card key={idx} className={idx === 0 ? "border-primary" : ""}>
                      <CardHeader>
                        {idx === 0 && <div className="text-xs font-semibold text-primary mb-2">EMPFEHLUNG</div>}
                        <CardTitle className="text-lg">{tariff.provider}</CardTitle>
                        <CardDescription>{tariff.rating}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold mb-4">ab {tariff.price} €<span className="text-sm font-normal text-muted-foreground">/Monat</span></div>
                        <Button asChild className="w-full" variant={idx === 0 ? "default" : "outline"}>
                          <a href={`/tarifwissen/${tariff.slug}`}>Details ansehen</a>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Nächste Schritte</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">1</div>
                      <div className="flex-1">
                        <div className="font-semibold">Kostenlose Beratung</div>
                        <div className="text-sm text-muted-foreground">Unsere Experten beraten Sie persönlich zur optimalen Absicherung</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">2</div>
                      <div className="flex-1">
                        <div className="font-semibold">Tarife vergleichen</div>
                        <div className="text-sm text-muted-foreground">Detaillierter Vergleich aller Tarife mit Leistungsübersicht</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">3</div>
                      <div className="flex-1">
                        <div className="font-semibold">PDF-Report herunterladen</div>
                        <div className="text-sm text-muted-foreground">Vollständige Auswertung und Tarifempfehlungen als PDF</div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 pt-4">
                      <Button asChild>
                        <a href="/termin-buchen">Kostenlose Beratung vereinbaren</a>
                      </Button>
                      <Button variant="outline" onClick={() => window.print()}>
                        <Download className="mr-2 h-4 w-4" />
                        PDF herunterladen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </form>
        </div>
      </main>

      <Footer />
      <FloatingContactWidget />
    </div>
  );
};

export default VersicherungsRechner;
